// Admin "view as" safety net (2026-09-25). While view mode is on, every fetch
// the page makes passes through decide(). The teacher and student data calls
// are already routed to the admin API by teacherApi / studentApi; this catches
// everything else. Deny by default:
//   - allowed: the admin API (view-as reads), Cognito (the admin's own token
//     refresh), read-only content lookups, same-origin GETs (the app's own
//     files), and GETs to non-backend hosts (fonts, images, CDNs);
//   - teacher view: the diagnostic monitor's two read actions;
//   - student view: practice-engine progress writes get a fake "saved" reply,
//     so try-it flows keep moving (nothing is sent);
//   - everything else to a backend, and every non-GET anywhere: blocked with a
//     423 "View only" reply, never sent. Any call that would use the admin's
//     own login to read "my" data (Buffy, placement, user flags...) is blocked
//     too, so the page never shows the admin's data as the person's.
// The viewer's own pages (admin pages, or a teacher's dashboard) are never
// guarded (view mode ends there anyway).
import { ADMIN_API_URL, PRACTICE_QUESTIONS_URL } from '../config/api';
import { getViewAs, isViewerHome, viewerOf } from './viewAsStore';
import { notifyBlocked, VIEW_ONLY_MESSAGE } from './viewAsApi';

const DIAG_HOST = 'gyxyzs5421.execute-api.us-east-1.amazonaws.com';
const DIAG_READS = new Set(['get_monitor_detail', 'get_class_status']);
const CONTENT_READS = [/\/retrieveConceptsDef$/];
// Practice-engine writes (StudentQuestionResponses progress): the homework
// player's missed-question walkthrough sends these. In view mode: fake OK.
const PRACTICE_WRITES = new Set([
  'saveSelection', 'recordAttempt', 'saveTelemetry', 'slideViewed', 'saveQuizAnswer', 'saveLessonInteraction',
  'markViewed', 'startLessonAttempt', 'saveLessonStep', 'finishLessonAttempt', 'startSpotAttempt',
  'finishSpotAttempt', 'saveStepResult', 'saveReflection',
]);
const BACKEND_HOST = /(\.execute-api\.[a-z0-9-]+\.amazonaws\.com|\.lambda-url\.[a-z0-9-]+\.on\.aws)$/i;
const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function actionOf(body) {
  if (typeof body !== 'string' || !body.startsWith('{')) return null;
  try { return JSON.parse(body)?.action ?? null; } catch { return null; }
}

const sameEndpoint = (u, raw) => {
  try {
    const e = new URL(raw, window.location.href);
    return u.origin === e.origin && u.pathname === e.pathname;
  } catch { return false; }
};

const ALLOW = { kind: 'allow' };
const block = (notify = false) => ({ kind: 'block', notify });
const fake = (json) => ({ kind: 'fake', json });

// { url, method, body } for one request while viewing as `va` -> what to do.
export function decide({ url, method = 'GET', body = null }, va) {
  let u;
  try { u = new URL(url, window.location.href); } catch { return block(); }
  const m = String(method || 'GET').toUpperCase();
  const read = READ_METHODS.has(m);
  const action = actionOf(body);

  if (sameEndpoint(u, ADMIN_API_URL)) return ALLOW;
  if (/\.amazoncognito\.com$/i.test(u.hostname) || /^cognito-idp\.[a-z0-9-]+\.amazonaws\.com$/i.test(u.hostname)) return ALLOW;
  if (u.hostname === DIAG_HOST) return va.kind === 'teacher' && DIAG_READS.has(action) ? ALLOW : block(!read);
  if (BACKEND_HOST.test(u.hostname) && CONTENT_READS.some((re) => re.test(u.pathname))) return ALLOW;
  if (va.kind === 'student' && sameEndpoint(u, PRACTICE_QUESTIONS_URL) && PRACTICE_WRITES.has(action)) {
    return fake({ ok: true, viewOnly: true });
  }
  if (u.origin === window.location.origin) return read ? ALLOW : block();
  if (BACKEND_HOST.test(u.hostname)) return block();
  return read ? ALLOW : block();
}

const json = (status, obj) => new Response(JSON.stringify(obj), { status, headers: { 'Content-Type': 'application/json' } });

let installed = false;
export function installFetchGuard() {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;
  const original = window.fetch.bind(window);
  window.fetch = function viewAsGuardedFetch(input, init) {
    const va = getViewAs();
    if (!va || isViewerHome(viewerOf(va), window.location.pathname)) return original(input, init);
    const isRequest = typeof Request !== 'undefined' && input instanceof Request;
    const url = isRequest ? input.url : String(input);
    const method = init?.method || (isRequest ? input.method : 'GET');
    const body = typeof init?.body === 'string' ? init.body : null;
    const verdict = decide({ url, method, body }, va);
    if (verdict.kind === 'allow') return original(input, init);
    if (verdict.kind === 'fake') return Promise.resolve(json(200, verdict.json));
    if (verdict.notify) notifyBlocked({ url });
    if (process.env.NODE_ENV === 'development') console.info('[view as] blocked', method, url);
    return Promise.resolve(json(423, { error: VIEW_ONLY_MESSAGE, viewOnly: true }));
  };
  // Beacons (telemetry on page hide) are never sent in view mode.
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const beacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = (url, data) => {
      const va = getViewAs();
      if (va && !isViewerHome(viewerOf(va), window.location.pathname)) return true;
      return beacon(url, data);
    };
  }
}
